import { CompetitorRelationListResponse, MarketPlace } from "@/api/types/main/schemas";
import { CompetitorMarked } from "@/components/custom-ui/competitor.marked";
import { MetricCell } from "@/components/custom-ui/metric.cell";
import { ActionCell } from "@/components/custom-ui/tables/action.cell";
import { ProductInfo } from "@/components/custom-ui/tables/product.info";
import { createColumnHelper } from "@tanstack/react-table";
import { MousePointerClick } from "lucide-react";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";

const columnHelper = createColumnHelper<CompetitorRelationListResponse>();

export const useGetCompetitorProductColumns = (date: string) => {
  const { sku: parentSku } = useParams<{ sku: string }>();
  const competitorsProductTableColumns = useMemo(
    () => [
      columnHelper.display({
        id: "action",
        header: "",
        enableHiding: false,
        size: 70,
        minSize: 70,
        cell: ({ row }) => {
          return (
            <ActionCell row={row}>
              {"sku" in row.original && (
                <>
                  <Link
                    to={`/services/competitors/${parentSku}/${row.original.sku}`}
                    className="hover:bg-accent p-2 rounded-sm duration-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MousePointerClick className="text-primary" size={20} />
                  </Link>
                </>
              )}
            </ActionCell>
          );
        },
      }),
      columnHelper.accessor(
        (row) => {
          return {
            isMarked: row.isMarked,
            competitorSku: row.sku,
          };
        },
        {
          id: "isMarked",
          header: "Конкурент",
          enableSorting: false,
          size: 120,
          minSize: 120,
          cell: ({ getValue }) => {
            const value = getValue();
            return (
              <CompetitorMarked
                isMarked={value.isMarked}
                sku={Number(parentSku)}
                competitorSku={value.competitorSku}
                date={date}
              />
            );
          },
        }
      ),
      columnHelper.accessor(
        (row) => {
          return {
            productName: row.productName,
            sku: row.sku,
            mpLink: row.MPLink,
            url: row.url,
            brand: row.brand,
            legalName: row.legalName,
          };
        },
        {
          id: "name",
          header: "Название конкурента",
          enableHiding: false,
          size: 350,
          minSize: 200,
          enableSorting: false,
          filterFn: (row, _, filterValue) => {
            const searchValue = String(filterValue).toLowerCase();
            const name = String(row.original.productName ?? "").toLowerCase();
            const sku = String(row.original.sku ?? "").toLowerCase();
            const brand = String(row.original.brand ?? "").toLowerCase();
            return (
              name.includes(searchValue) || sku.includes(searchValue) || brand.includes(searchValue)
            );
          },
          cell: ({ getValue }) => {
            const value = getValue();
            return (
              <ProductInfo
                marketplace={MarketPlace.WB}
                name={value.productName || "-"}
                sku={value.sku}
                productId={value.sku}
                parentRoute="/services/competitors"
                url={value.url}
                mpstatLink={value.mpLink}
              />
            );
          },
        }
      ),
      columnHelper.accessor("revenue", {
        id: "revenue",
        header: "Выручка",
        size: 150,
        minSize: 100,
        cell: ({ getValue }) => {
          const value = getValue();
          return <MetricCell value={value} unit="rub" />;
        },
      }),
      columnHelper.accessor("brand", {
        id: "brand",
        header: "Бренд",
        size: 150,
        minSize: 100,
        cell: ({ getValue }) => {
          const value = getValue();
          return <span className="whitespace-nowrap truncate">{value ?? "-"}</span>;
        },
      }),
      columnHelper.accessor("categoryPercent", {
        id: "categoryPercent",
        header: "Процент от выручки в категории",
        size: 150,
        minSize: 100,
        cell: ({ getValue }) => {
          const value = getValue();
          return <MetricCell value={value} unit="percent" />;
        },
      }),
      columnHelper.accessor("orderCount", {
        id: "orderCount",
        header: "Количество заказов",
        size: 150,
        minSize: 100,
        cell: ({ getValue }) => {
          const value = getValue();
          return <MetricCell value={value} unit="pieces" />;
        },
      }),
      columnHelper.accessor("avgPrice", {
        id: "avgPrice",
        header: "Средний чек",
        size: 150,
        minSize: 100,
        cell: ({ getValue }) => {
          const value = getValue();
          return <MetricCell value={value} unit="rub" />;
        },
      }),
      columnHelper.accessor("rating", {
        id: "rating",
        header: "Рейтинг",
        size: 150,
        minSize: 100,
      }),
      columnHelper.accessor("reviewCount", {
        id: "reviewCount",
        header: "Количество отзывов",
        size: 150,
        minSize: 100,
        cell: ({ getValue }) => {
          const value = getValue();
          return <MetricCell value={value} unit="pieces" />;
        },
      }),
      columnHelper.accessor("daysInStock", {
        id: "daysInStock",
        header: "Дней в наличии",
        size: 150,
        minSize: 100,
      }),
      columnHelper.accessor("categoryPosition", {
        id: "categoryPosition",
        header: "Позиция в категории",
        size: 150,
        minSize: 100,
      }),
      columnHelper.accessor("avgAdPosition", {
        id: "avgAdPosition",
        header: "Средняя позиция рекламы",
        size: 150,
        minSize: 100,
      }),
      columnHelper.accessor("avgOrganicPosition", {
        id: "avgOrganicPosition",
        header: "Средняя органическая позиция",
        size: 150,
        minSize: 100,
      }),
    ],
    [date, parentSku]
  );
  return competitorsProductTableColumns;
};
